import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import BeforeAfterGallery from '@/components/BeforeAfterGallery';
import Button from '@/components/Button';
import Booking from '@/components/Booking';
import PriceCard from '@/components/PriceCard';
import { getServiceOptions } from '@/screens/services/NewServices';
import { getServicePricing } from '@/screens/services/NewPrices';
import { useTranslation } from 'react-i18next';

// Import fabric images for the sub-service pages
import sofaImg from '@/assets/images/sofa-bg-1.jpeg';
import tilamImg from '@/assets/images/tilam-bg-1.jpeg';
import keretaImg from '@/assets/images/kereta-kerusi-bg-1.jpeg';
import lainLainImg from '@/assets/images/carpet-bg-1.jpeg';

// Import specific before/after images for each sub-service
// Sofa before/after images
import sofaBeforeImg1 from '@/assets/images/fabric-before-2.jpeg';
import sofaAfterImg1 from '@/assets/images/fabric-after-2.jpeg';
import sofaBeforeImg2 from '@/assets/images/fabric-before-3.jpeg';
import sofaAfterImg2 from '@/assets/images/fabric-after-3.jpeg';
import sofaBeforeImg3 from '@/assets/images/sofa-before-3.jpeg';
import sofaAfterImg3 from '@/assets/images/sofa-after-3.jpeg';
import sofaBeforeImg4 from '@/assets/images/sofa-before-4.jpeg';
import sofaAfterImg4 from '@/assets/images/sofa-after-4.jpeg';
import sofaBeforeImg5 from '@/assets/images/sofa-before-5.jpeg';
import sofaAfterImg5 from '@/assets/images/sofa-after-5.jpeg';

// Tilam before/after images
import tilamBeforeImg1 from '@/assets/images/fabric-before-1.jpeg';
import tilamAfterImg1 from '@/assets/images/fabric-after-1.jpeg';
import tilamBeforeImg2 from '@/assets/images/tilam-before-2.jpeg';
import tilamAfterImg2 from '@/assets/images/tilam-after-2.jpeg';
import tilamBeforeImg3 from '@/assets/images/tilam-before-3.jpeg';
import tilamAfterImg3 from '@/assets/images/tilam-after-3.jpeg';
import tilamBeforeImg4 from '@/assets/images/tilam-before-4.jpeg';
import tilamAfterImg4 from '@/assets/images/tilam-after-4.jpeg';

// Kereta before/after images
import keretaBeforeImg1 from '@/assets/images/fabric-before-4.jpeg';
import keretaAfterImg1 from '@/assets/images/fabric-after-4.jpeg';
import keretaBeforeImg2 from '@/assets/images/kerusikereta-after-2.jpeg';
import keretaAfterImg2 from '@/assets/images/kerusikereta-after-2.jpeg';
import keretaBeforeImg3 from '@/assets/images/kerusikereta-after-3.jpeg';
import keretaAfterImg3 from '@/assets/images/kerusikereta-after-3.jpeg';
import keretaBeforeImg4 from '@/assets/images/kerusikereta-after-4.jpeg';
import keretaAfterImg4 from '@/assets/images/kerusikereta-after-4.jpeg';
import keretaBeforeImg5 from '@/assets/images/atapkereta-before-1.jpeg';
import keretaAfterImg5 from '@/assets/images/atapkereta-after-1.jpeg';
import keretaBeforeImg6 from '@/assets/images/atapkereta-before-2.jpeg';
import keretaAfterImg6 from '@/assets/images/atapkereta-after-2.jpeg';

// Lain-lain before/after images
import lainBeforeImg3 from '@/assets/images/stroller-before-1.jpeg';
import lainAfterImg3 from '@/assets/images/stroller-after-1.jpeg';
import lainBeforeImg4 from '@/assets/images/stroller-before-2.jpeg';
import lainAfterImg4 from '@/assets/images/stroller-after-2.jpeg';
import lainBeforeImg5 from '@/assets/images/carpet-before-1.jpeg';
import lainAfterImg5 from '@/assets/images/carpet-after-1.jpeg';


// Define the interface for BeforeAfterImage to match what's in your BeforeAfterGallery component
interface BeforeAfterImage {
  id: string;
  beforeImage: string;
  afterImage: string;
  alt?: string;
  description?: string;
}

// Define the interface for fabric sub-service configuration
interface FabricSubServiceConfig {
  title?: string; // Optional - comes from translations
  description?: string; // Optional - comes from translations
  backgroundImage: string;
  serviceIds: string[]; // Use card IDs instead of service names
  serviceDescriptions?: { [key: string]: string }; // Optional - comes from translations
  images: BeforeAfterImage[]; // This will hold the specific before/after images for each sub-service
  showAddons: boolean;
  addonFilter?: (addonTitle: string) => boolean;
  excludedServices?: string[]; // Optional - comes from translations
}

// Define the fabric sub-services data with proper typing and specific before/after images
const fabricSubServices: Record<string, FabricSubServiceConfig> = {
  'sofa': {
    backgroundImage: sofaImg,
    serviceIds: ['sofa-1-seater', 'sofa-2-seater', 'sofa-3-seater', 'sofa-l-shape-3-seater'],
    images: [
      {
        id: 'sofa-1',
        beforeImage: sofaBeforeImg1,
        afterImage: sofaAfterImg1,
        alt: 'Sofa cleaning before and after',
        description: ''
      },
      {
        id: 'sofa-2',
        beforeImage: sofaBeforeImg2,
        afterImage: sofaAfterImg2,
        alt: 'Sofa cleaning before and after',
        description: ''
      },
      {
        id: 'sofa-3',
        beforeImage: sofaBeforeImg3,
        afterImage: sofaAfterImg3,
        alt: 'Sofa cleaning before and after',
        description: ''
      },
      {
        id: 'sofa-4',
        beforeImage: sofaBeforeImg4,
        afterImage: sofaAfterImg4,
        alt: 'Sofa cleaning before and after',
        description: ''
      },
      {
        id: 'sofa-5',
        beforeImage: sofaBeforeImg5,
        afterImage: sofaAfterImg5,
        alt: 'Sofa cleaning before and after',
        description: ''
      }
    ],
    showAddons: false
  },
  'tilam': {
    backgroundImage: tilamImg,
    serviceIds: ['mattress-single-one-side', 'mattress-single-both-sides', 'mattress-queen-one-side', 'mattress-queen-both-sides', 'mattress-king-one-side', 'mattress-king-both-sides'],
    images: [
      {
        id: 'tilam-1',
        beforeImage: tilamBeforeImg1,
        afterImage: tilamAfterImg1,
        alt: 'Mattress cleaning before and after',
        description: ''
      },
      {
        id: 'tilam-2',
        beforeImage: tilamBeforeImg2,
        afterImage: tilamAfterImg2,
        alt: 'Mattress cleaning before and after',
        description: ''
      },
      {
        id: 'tilam-3',
        beforeImage: tilamBeforeImg3,
        afterImage: tilamAfterImg3,
        alt: 'Mattress cleaning before and after',
        description: ''
      },
      {
        id: 'tilam-4',
        beforeImage: tilamBeforeImg4,
        afterImage: tilamAfterImg4,
        alt: 'Mattress cleaning before and after',
        description: ''
      }
    ],
    showAddons: false
  },
  'kereta': {
    backgroundImage: keretaImg,
    serviceIds: ['car-seat-5-seater', 'car-seat-7-seater'],
    images: [
      {
        id: 'kereta-1',
        beforeImage: keretaBeforeImg1,
        afterImage: keretaAfterImg1,
        alt: 'Car seat cleaning before and after',
        description: ''
      },
      {
        id: 'kereta-2',
        beforeImage: keretaBeforeImg2,
        afterImage: keretaAfterImg2,
        alt: 'Car seat cleaning before and after',
        description: ''
      },
      {
        id: 'kereta-3',
        beforeImage: keretaBeforeImg3,
        afterImage: keretaAfterImg3,
        alt: 'Car seat cleaning before and after',
        description: ''
      },
      {
        id: 'kereta-4',
        beforeImage: keretaBeforeImg4,
        afterImage: keretaAfterImg4,
        alt: 'Car seat cleaning before and after',
        description: ''
      },
      {
        id: 'kereta-5',
        beforeImage: keretaBeforeImg5,
        afterImage: keretaAfterImg5,
        alt: 'Car seat cleaning before and after',
        description: ''
      },
      {
        id: 'kereta-6',
        beforeImage: keretaBeforeImg6,
        afterImage: keretaAfterImg6,
        alt: 'Car seat cleaning before and after',
        description: ''
      }
    ],
    showAddons: true,
    addonFilter: (addonTitle: string) => addonTitle.includes('Bumbung') || addonTitle.includes('Dalaman') || addonTitle.includes('Ceiling') || addonTitle.includes('Interior')
  },
  'lain-lain': {
    backgroundImage: lainLainImg,
    serviceIds: ['carpet', 'baby-stroller', 'baby-car-seat', 'dining-chair'],
    images: [
      {
        id: 'lain-3',
        beforeImage: lainBeforeImg3,
        afterImage: lainAfterImg3,
        alt: 'Other fabric cleaning before and after',
        description: ''
      },
      {
        id: 'lain-4',
        beforeImage: lainBeforeImg4,
        afterImage: lainAfterImg4,
        alt: 'Other fabric cleaning before and after',
        description: ''
      },
      {
        id: 'lain-5',
        beforeImage: lainBeforeImg5,
        afterImage: lainAfterImg5,
        alt: 'Other fabric cleaning before and after',
        description: ''
      }
    ],
    showAddons: false
  },
};

interface ServiceWithPrice {
  id: string;
  title: string;
  price: string;
}

const FabricSubServicePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [selectedServices, setSelectedServices] = useState<ServiceWithPrice[]>([]);

  // Get the sub-service data based on the slug
  const subServiceBase = slug ? fabricSubServices[slug] : null;

  // Get translated fabric sub-service data
  const fabricSubServicesTranslated = t('fabricSubServices', { returnObjects: true }) as Array<{
    title: string;
    slug: string;
    description: string;
    serviceDescriptions?: { [key: string]: string };
    excludedServices?: string[];
    imageDescriptions?: string[];
  }>;

  // Find the translated data for this slug
  const translatedData = fabricSubServicesTranslated.find(service => service.slug === slug);

  // Merge hardcoded structure with translated text
  const subService = subServiceBase && translatedData ? {
    ...subServiceBase,
    title: translatedData.title,
    description: translatedData.description,
    serviceDescriptions: translatedData.serviceDescriptions,
    excludedServices: translatedData.excludedServices || [],
    // Merge image descriptions from translations
    images: subServiceBase.images.map((img, index) => ({
      ...img,
      description: translatedData.imageDescriptions?.[index] || img.description || ''
    }))
  } : subServiceBase;

  // Get the fabric service data and pricing with translations
  const serviceOptions = getServiceOptions(t);
  const fabricService = serviceOptions['fabric'];
  const servicePricing = getServicePricing(t);
  const fabricPricing = servicePricing['fabric'];
  
  // Filter price cards based on the sub-service
  const getServiceCards = () => {
    if (!subService) return [];

    return fabricPricing.cards.filter(card =>
      subService.serviceIds.includes(card.id)
    );
  };

  // Get add-on cards - only if this sub-service should show add-ons
  const getAddonCards = () => {
    if (!subService || !subService.showAddons) return [];

    const addonCards = fabricPricing.cards.filter(card => card.id.startsWith('addon-'));

    // If there's an addon filter function, apply it
    if (subService.addonFilter) {
      return addonCards.filter(card => subService.addonFilter!(card.title));
    }

    return addonCards;
  };

  const handleBookingClick = (serviceIds: string[] = []) => {
    if (serviceIds.length === 0) {
      setIsBookingOpen(true);
      return;
    }

    // Find the service details from pricing data by ID
    const serviceDetails = fabricPricing.cards.find(
      card => card.id === serviceIds[0]
    );

    if (serviceDetails) {
      setSelectedServices([{
        id: serviceDetails.id,
        title: serviceDetails.title,
        price: serviceDetails.price
      }]);
    }
    setIsBookingOpen(true);
  };

  const handleBackClick = () => {
    navigate('/services/fabric', { state: { skipLoading: true } });
  };

  if (!subService) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">{t('common.serviceNotFound', 'Service not found')}</h2>
          <button
            onClick={handleBackClick}
            className="bg-trhine-yellow-300 text-black px-4 py-2 rounded-md"
          >
            ← {t('serviceDetails.fabric.title')}
          </button>
        </div>
      </div>
    );
  }

  const serviceCards = getServiceCards();
  const addonCards = getAddonCards();

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${subService.backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        className="min-h-[50vh] flex flex-col justify-center relative"
      >
        <div className="container mx-auto px-4 py-16">
          <button
            onClick={handleBackClick}
            className="text-white mb-4 flex items-center pt-14"
          >
            <span className="mr-2">←</span> {t('serviceDetails.fabric.previousPage')}
          </button>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">{subService.title}</h1>
          <p className="text-lg text-white max-w-3xl mb-8">{subService.description}</p>
          <Button
            text={t('common.bookNow')}
            active={true}
            className="bg-trhine-yellow-300 text-black"
            onClick={() => handleBookingClick()}
            serviceId="fabric"
          />
        </div>
      </section>
      
      {/* Before After Gallery Section - Moved before pricing section */}
      {subService.images.length > 0 && (
        <BeforeAfterGallery
          images={subService.images}
          activeButtonColor={fabricService.beforeAfterButtonColor}
          title={`${t('common.beforeAfterTitle')} ${subService.title}`}
        />
      )}

      {/* Pricing Section - Using the same format as in ServicePage */}
      <div className={`pt-8 py-32 px-8 ${fabricPricing.backgroundColor}`}>
        <div className="max-w-[1200px] mx-auto">
          <h1 className="text-[40px] font-bold text-center text-black mb-8">{t('servicePage.servicePriceTitle')}</h1>

          {/* General Features Section - Now Dynamic */}
          {subService.excludedServices && subService.excludedServices.length > 0 && (
            <div className="mb-12">
              <div className="bg-gray-800 rounded-lg p-6 max-w-2xl mx-auto">
                <h2 className="text-white text-xl font-semibold mb-4">{t('servicePage.excludedServices')}</h2>
                <ul className="list-disc pl-5 space-y-2">
                  {subService.excludedServices.map((feature, idx) => (
                    <li key={idx} className="text-white text-[16px]">{feature}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          {/* Main pricing cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {serviceCards.map((card) => (
              <PriceCard
                key={card.id}
                {...card}
                buttonClassName={fabricPricing.buttonClassName}
                onBookingClick={() => handleBookingClick([card.id])}
              />
            ))}
          </div>

          {/* Add-Ons section - only shown for certain sub-services */}
          {addonCards.length > 0 && (
            <div className="mt-16">
              <h2 className="text-[32px] font-bold text-center text-black mb-8">{t('servicePage.addOns')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {addonCards.map((card) => (
                  <div key={card.id} className="bg-gray-800 rounded-tr-[24px] rounded-bl-[24px] p-6 w-full">
                    <h3 className="text-white text-[20px] font-bold mb-4">
                      {card.title}
                    </h3>
                    <ul className="list-disc pl-5 space-y-2 mb-6">
                      {card.features?.map((feature: string, idx: number) => (
                        <li key={idx} className="text-white text-[14px]">{feature}</li>
                      ))}
                    </ul>
                    <div className="flex justify-between items-center">
                      <span className="text-white text-[20px] font-bold">{card.price}</span>
                      <button
                        className={`${fabricPricing.buttonClassName} text-[12px] px-4 py-1.5`}
                        onClick={() => handleBookingClick([card.id])}
                      >
                        {t('servicePage.book')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      <Booking 
        isOpen={isBookingOpen}
        onClose={() => {
          setIsBookingOpen(false);
          setSelectedServices([]);
        }}
        serviceId="fabric"
        selectedServices={selectedServices}
      />
      
      <Footer 
        footer_bg="bg-trhine-yellow-100"
        text_color="text-black"
        currentService="fabric"
      />
    </div>
  );
};

export default FabricSubServicePage;